import { styled, Theme } from "@mui/material";
import Box from "@mui/material/Box";
import Tab from "@mui/material/Tab";
import Typography from "@mui/material/Typography";
import useMediaQuery from "@mui/material/useMediaQuery";
import { OrderDirection } from "_shared/_api/ordering/order-direction";
import PaginatedResponse from "_shared/_api/pagination/responses/paginated-response";
import PaginationParameters from "_shared/_api/queries/pagination/pagination-parameters";
import { useDialog } from "_shared/_providers/dialogs/dialog";
import CondensedCell from "_shared/_views/tables/condensed-cell";
import RowActions from "_shared/_views/tables/row-actions";
import RowLink from "_shared/_views/tables/row-link";
import SearchMatch from "_shared/_views/tables/search-match";
import { MRTNoMemo } from "_shared/_views/tables/table";
import { emptyRowsFallback } from "_shared/_views/tables/table-empty-state";
import Guid from "_shared/guids/guid";
import IndividualAvatar from "marketplace/_views/avatars/individual-avatar";
import ViewIndividualProfile from "marketplace/individuals/profiles/_views/individual-profile-container";
import IndividualProfile from "marketplace/individuals/profiles/individual-profile";
import {
  type MRT_ColumnFiltersState as ColumnFiltersState,
  createMRTColumnHelper,
  type MRT_TableOptions,
  type MRT_PaginationState as PaginationState,
  type MRT_Row as Row,
  type MRT_TableInstance,
  type MRT_SortingState as SortingState,
  MRT_GlobalFilterTextField as GlobalFilterTextField,
  type MRT_Updater,
} from "material-react-table";
import { applyUpdater } from "_shared/_views/tables/table-state-updater";
import { ToolbarButtonsContainer } from "_shared/_views/tables/toolbar-buttons";
import {
  MobileToolbarButtonsContainer,
  searchFieldSpacing,
  ScrollableTabs,
  searchTextFieldProps,
  topToolbarFitsThePage,
  ToolbarButtons,
} from "_shared/_views/tables/table-toolbar";
import { TableToolsMenu } from "_shared/_views/tables/table-tools-menu";
import React, { useCallback, useEffect, useMemo } from "react";
import { useLoaderData, useNavigate } from "react-router";
import UserCoworkerQuery, {
  UserCoworkerFilterParameters,
  USER_COWORKER_QUERY_MAXIMUM_LENGTH,
  UserCoworkerOrderField,
  UserCoworkerOrderParameters,
} from "users/information/coworker-information/_api/commands/query-user-coworker-api-request";
import { skipToken } from "@reduxjs/toolkit/query";
import { useGetUserCoworkersQuery } from "users/information/coworker-information/_api/user-coworker-api";
import UserCoworker from "users/information/coworker-information/user-coworker";
import { PageTab } from "users/networking/_views/network-page-tabs";

const TableTopToolbarContainer = styled("div")(() => ({
  maxWidth: "100%",
  minWidth: 0,
  display: "flex",
  flexDirection: "column",
  width: "100%",
}));
const LowerTopToolbarContainer = styled("div")(() => ({
  minWidth: 0,
  display: "flex",
  flexDirection: "row",
  alignItems: "left",
  width: "100%",
}));
const TableTitle = styled(Typography)(({ theme }) => ({
  paddingLeft: theme.spacing(1),
}));
const NameCellContainer = styled(Box)(({ theme }) => ({
  alignItems: "center",
  display: "flex",
  gap: theme.spacing(1.5),
  minWidth: 0,
}));

const columnHelper = createMRTColumnHelper<UserCoworker>();

/** The Name column's id. The server sorts it by last name, and filters it by first or last name. */
export const NAME_COLUMN = "name";

/**
 * The Company column's id. The server sorts by entity name, but filters only by an entity id the column
 * does not show. Hidden until the reader chooses it: the company reads under the name meanwhile (#6722).
 */
export const LEGAL_ENTITY_COLUMN = "entityName";

/** The Email column's id. It folds under the name where the pane is short of room (#6722). */
export const EMAIL_COLUMN = "email";

/** Where a coworker's profile opens: the row, the name and the ⋯'s Open all go here. */
export function coworkerDestination(coworker: UserCoworker): string {
  return `/network/coworkers/view/${coworker.individualId}`;
}

/** The longest name filter the server accepts (`UserCoworkerFilterParameters.Name`). */
export const COWORKER_NAME_FILTER_MAXIMUM_LENGTH = 200;

/** By name, A to Z: the order the server is asked for until the reader picks another. */
export const DEFAULT_SORTING: SortingState = [{ id: NAME_COLUMN, desc: false }];

export function createCoworkerFilters(name?: string, email?: string): UserCoworkerFilterParameters {
  return new UserCoworkerFilterParameters(name, email);
}

export function isCoworkerGlobalFilterValid(value: string): boolean {
  return value.length <= USER_COWORKER_QUERY_MAXIMUM_LENGTH;
}

/**
 * The server's order, from the table's sort state (#5453). Each sortable column is mapped to a field
 * of the enum, so a column id is never sent as an order field: the Name column's id is "name", which
 * `UserCoworkerOrderParameters` refuses. Always one column and a direction, because the server
 * orders every page.
 */
export function coworkerOrderFrom(sorting: SortingState): UserCoworkerOrderParameters {
  const first = sorting.length > 0 ? sorting[0] : DEFAULT_SORTING[0];
  return new UserCoworkerOrderParameters(
    first.id === LEGAL_ENTITY_COLUMN ? UserCoworkerOrderField.EntityName : UserCoworkerOrderField.Name,
    first.desc ? OrderDirection.Descending : OrderDirection.Ascending,
  );
}

/** The server's filters, from the table's column filters, or undefined when they cannot be sent. */
export function coworkerFiltersFrom(columnFilters: ColumnFiltersState): UserCoworkerFilterParameters | undefined {
  const value = columnFilters.find((filter) => filter.id === NAME_COLUMN)?.value;
  const name = typeof value === "string" ? value.trim() : "";
  if (name.length > COWORKER_NAME_FILTER_MAXIMUM_LENGTH) return undefined;
  return createCoworkerFilters(name || undefined);
}

export default function CoworkersTab() {
  const handledRef = React.useRef<string | null>(null);

  const [globalFilter, setGlobalFilter] = React.useState("");
  const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>([]);
  const [sorting, setSorting] = React.useState<SortingState>(DEFAULT_SORTING);
  const [pagination, setPagination] = React.useState<PaginationState>({
    pageIndex: 0,
    pageSize: 25,
  });

  const { openDialog } = useDialog();
  const navigate = useNavigate();
  const isMediumDisplaySize = useMediaQuery((theme: Theme) => theme.breakpoints.down("md"));

  // Route
  const routeParams = useLoaderData();
  const routeAction = routeParams?.action as string | undefined;
  const routeId = routeParams?.id as string | undefined;

  // #region Queries
  const isGlobalFilterValid = isCoworkerGlobalFilterValid(globalFilter);
  // The Company column contains a display name, while the API accepts only a legal-entity
  // Guid, so only the name is filtered.
  const filterParameters = useMemo(() => coworkerFiltersFrom(columnFilters), [columnFilters]);

  const query = useMemo(() => {
    if (!isGlobalFilterValid || !filterParameters) return undefined;

    const paginationParameters = PaginationParameters.create(pagination);
    return new UserCoworkerQuery(filterParameters, coworkerOrderFrom(sorting), paginationParameters, globalFilter);
  }, [pagination, filterParameters, sorting, globalFilter, isGlobalFilterValid]);

  const coworkerQuery = useGetUserCoworkersQuery(
    query ? { query, append: false } : skipToken,
  );

  const coworkers = useMemo(() => {
    if (!coworkerQuery.data) {
      return PaginatedResponse.empty<UserCoworker>();
    }
    return PaginatedResponse.from<UserCoworker>(
      UserCoworker.fromJSONArray(coworkerQuery.data.data),
      coworkerQuery.data.headers,
    );
  }, [coworkerQuery.data]);

  const handleViewProfile = useCallback(
    (individualId?: Guid, data?: UserCoworker) => {
      if (!individualId || !data) return;

      const individualProfile = new IndividualProfile({
        userId: data.id,
        firstName: data.name.firstName ?? "",
        lastName: data.name.lastName ?? "",
        avatarId: data.avatarId,
        legalEntityId: data.legalEntityId,
        email: data.email,
      });

      openDialog({
        titleStyle: {
          paddingBottom: 0,
        },
        contentSxProps: {
          display: "flex",
          overflowX: "hidden",
        },
        MuiProps: {
          maxWidth: "lg",
          fullWidth: false,
        },
        onClose: () => {
          const parentPath = new URL("..", globalThis.location.origin + globalThis.location.pathname);
          navigate(parentPath.pathname);
        },
        component: <ViewIndividualProfile individualId={individualId} profile={individualProfile} />,
      });
    },
    [navigate, openDialog],
  );

  // #region Lifecycle
  useEffect(() => {
    // A route that names nobody is the dialog having closed, so forget what it opened: otherwise
    // clicking the same coworker again finds its key handled and opens nothing.
    if (routeAction !== "view" || !routeId) {
      handledRef.current = null;
      return;
    }

    const key = `${routeAction}:${routeId}`;
    if (handledRef.current === key) return;

    const match = coworkers.data.find((c) => c.individualId?.value === routeId);
    if (!match) return;

    handledRef.current = key;
    handleViewProfile(new Guid(routeId), match);
  }, [routeAction, routeId, coworkers.data, handleViewProfile]);
  // #endregion

  // The company reads under the name while its own column is hidden, which it is until the reader
  // chooses it: shown, the column has it to itself again (#6722).
  const renderContactNameColumn = useCallback(
    (user: UserCoworker, table: MRT_TableInstance<UserCoworker>) => {
      const isCompanyUnderName = table.getState().columnVisibility?.[LEGAL_ENTITY_COLUMN] === false;
      return (
        <NameCellContainer>
          <IndividualAvatar individual={user.asIndividual() ?? null} />
          <CondensedCell
            title={
              <RowLink to={coworkerDestination(user)}>
                <SearchMatch text={user.name.fullName} table={table} />
              </RowLink>
            }
            secondary={isCompanyUnderName ? user.entityName : undefined}
          />
        </NameCellContainer>
      );
    },
    [],
  );

  const rowActions = useCallback((row: Row<UserCoworker>) => {
    // MRT draws placeholder rows with no coworker while the first page loads.
    if (!row.original.individualId) return null;
    return (
      <RowActions
        ariaLabel={`Actions for ${row.original.name.fullName}`}
        open={{ to: coworkerDestination(row.original) }}
        actions={[]}
      />
    );
  }, []);

  // Accessor columns, because TanStack never sorts or filters a display column (#5433). The server
  // sorts and filters; the accessors only let the table offer the controls.
  const columns = useMemo(() => [
    // MRT draws placeholder rows with a null name while the first page loads, and sorting reads them.
    columnHelper.accessor((coworker) => coworker.name?.fullName ?? "", {
      header: "Name",
      id: NAME_COLUMN,
      enableSorting: true,
      enableColumnFilter: true,
      muiFilterTextFieldProps: { slotProps: { htmlInput: { maxLength: COWORKER_NAME_FILTER_MAXIMUM_LENGTH } } },
      meta: { kind: "name" },
      Cell: ({ row, table }) => renderContactNameColumn(row.original, table),
    }),
    // The search matches emails, so MRT marks the term here. The server neither sorts nor filters by it.
    columnHelper.accessor((coworker) => coworker.email?.toString() ?? "", {
      header: "Email",
      id: EMAIL_COLUMN,
      enableSorting: false,
      enableColumnFilter: false,
      meta: {
        kind: "text",
        fold: { into: NAME_COLUMN, priority: 1, render: (coworker) => coworker.email?.toString() },
      },
    }),
    columnHelper.accessor((coworker) => coworker.entityName ?? "", {
      header: "Company",
      id: LEGAL_ENTITY_COLUMN,
      enableSorting: true,
      enableColumnFilter: false,
      // The search matches names and emails, never the entity: no mark here (#6590).
      enableGlobalFilter: false,
      meta: { kind: "text" },
    }),
  ], [renderContactNameColumn]);

  const tableOptions: MRT_TableOptions<UserCoworker> = {
    columns,
    data: coworkers.data,
    getRowId: (row: UserCoworker) => row.individualId?.value ?? row.id?.value ?? "",
    enableRowSelection: false,
    enableTableHead: true,
    enableRowActions: true,
    enableColumnPinning: true,
    renderRowActions: ({ row }) => rowActions(row),

    renderToolbarInternalActions: ({ table }) =>
      !isMediumDisplaySize && (
        <ToolbarButtonsContainer>
          <ToolbarButtons>
            <GlobalFilterTextField table={table} sx={searchFieldSpacing} />
            <TableToolsMenu table={table} />
          </ToolbarButtons>
        </ToolbarButtonsContainer>
      ),
    renderTopToolbarCustomActions: ({ table }) => (
      <TableTopToolbarContainer>
        <TableTitle variant="h4">Network</TableTitle>

        <LowerTopToolbarContainer>
          <ScrollableTabs
            value={PageTab.Coworkers}
            indicatorColor="primary"
            textColor="primary"
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
            onChange={(_event, newValue) => navigate(`/network/${newValue}`)}
          >
            <Tab value={PageTab.Contacts} label="Contacts" />
            <Tab value={PageTab.Coworkers} label="Coworkers" />
            <Tab value={PageTab.Pending} label="Pending" />
            <Tab value={PageTab.Groups} label="Groups" />
          </ScrollableTabs>
        </LowerTopToolbarContainer>

        {isMediumDisplaySize && (
          <MobileToolbarButtonsContainer>
            <GlobalFilterTextField table={table} />
            <ToolbarButtons>
              <TableToolsMenu table={table} />
            </ToolbarButtons>
          </MobileToolbarButtonsContainer>
        )}
      </TableTopToolbarContainer>
    ),

    initialState: {
      showGlobalFilter: true,
      columnPinning: { right: ["mrt-row-actions"] },
      columnVisibility: { [LEGAL_ENTITY_COLUMN]: false },
    },
    positionGlobalFilter: "none",
    muiSearchTextFieldProps: ({ table }) => ({
      ...searchTextFieldProps({
        searches: "coworkers",
        table,
        maxLength: USER_COWORKER_QUERY_MAXIMUM_LENGTH,
      }),
      error: !isGlobalFilterValid,
      helperText: !isGlobalFilterValid
        ? `Search must be ${USER_COWORKER_QUERY_MAXIMUM_LENGTH} characters or fewer.`
        : undefined,
    }),
    renderEmptyRowsFallback: emptyRowsFallback({
      isError: coworkerQuery.isError,
      isLoading: coworkerQuery.isFetching,
      onRetry: () => void coworkerQuery.refetch(),
      subject: "coworkers",
    }),

    manualFiltering: true,
    manualPagination: true,
    manualSorting: true,
    // The server orders by one column, and always by one, so there is neither a second sort nor none.
    enableMultiSort: false,
    enableSortingRemoval: false,
    enableStickyHeader: true,
    enableStickyFooter: true,

    muiTableBodyRowProps: ({ row }) => ({
      onClick: () => { navigate(coworkerDestination(row.original)); },
      sx: { cursor: "pointer" },
    }),

    muiTableContainerProps: { sx: { flex: 1 } },
    muiTopToolbarProps: { sx: topToolbarFitsThePage },
    muiTablePaperProps: { sx: { height: "100%", display: "flex", flexDirection: "column" } },

    muiToolbarAlertBannerProps: coworkerQuery.isError
      ? { color: "error" as const, children: "Error loading data" }
      : undefined,

    rowCount: coworkers.totalElements,

    // A new search, filter or order is a new list, and a new list starts on its first page.
    onColumnFiltersChange: (updater: MRT_Updater<ColumnFiltersState>) => {
      setColumnFilters(applyUpdater(updater, columnFilters));
      setPagination((current) => ({ ...current, pageIndex: 0 }));
    },
    onGlobalFilterChange: (updater: MRT_Updater<string>) => {
      setGlobalFilter(applyUpdater(updater, globalFilter) ?? "");
      setPagination((current) => ({ ...current, pageIndex: 0 }));
    },
    onPaginationChange: setPagination,
    onSortingChange: (updater: MRT_Updater<SortingState>) => {
      const next = applyUpdater(updater, sorting);
      setSorting(next.length > 0 ? next : DEFAULT_SORTING);
      setPagination((current) => ({ ...current, pageIndex: 0 }));
    },

    state: {
      globalFilter,
      columnFilters,
      isLoading: coworkerQuery.isLoading,
      pagination,
      showAlertBanner: coworkerQuery.isError,
      showProgressBars: coworkerQuery.isFetching,
      showSkeletons: coworkerQuery.isLoading,
      sorting,
    },
  };

  return <MRTNoMemo<UserCoworker> options={tableOptions} cards={{ defaultSorting: DEFAULT_SORTING }} />;
}
